import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment-timezone';

import { openModalAction } from '../../../actions/modal';
import { PageHeader } from '../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { TextInput } from '../../../components/PageHeader/TextInput';
import { DATE_MONTH_DAY_YEAR_WITH_DASH } from '../../../constants';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import Strings from '../../../Strings';
import { actions } from '../../Kits/redux/actions';
import { GET_KIT_DETAILS_RESULT, UPDATE_KIT } from '../../Kits/redux/constants';
import './Devices.scss';
import { convertUnixEpochToHumanReadable, getVitalsData } from '../../../utils';
import { getUrlForDevice } from './HubDeviceEvents';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition } from '../../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { getFiltersForRequest } from '../../../components/AdvancedFilters/helpers';
import { PERMISSIONS, hasPermission } from '../../../utils/userPermissions';

function KitDevices(props) {
  const kitId = encodeURIComponent(props.match.params.id);
  const defaultFilter = 'status:active';
  const pageRequest = React.useRef({ filterBy: defaultFilter });
  const refreshEnable = React.useRef(true);

  const [kitName, setKitName] = React.useState('');
  const [org, setOrg] = React.useState();

  useEffect(() => {
    return () => props.clearData();
  }, []);

  const getDevices = () => {
    props.getKitDevices(kitId, pageRequest.current);
    getKitDetails(kitId);
  };

  useEffect(() => {
    onFiltersChange(props.filters);
  }, [props.filters]);

  const onFiltersChange = filters => {
    return filtersDebounced(filters);
  };

  const filtersDebounced = _.debounce(() => {
    const newFiltersForRequest = getFiltersForRequest(props.filters).map(f => f.replace('DeviceStatus', 'status'));
    if (JSON.stringify(newFiltersForRequest) !== JSON.stringify(pageRequest.current.filterBy)) {
      if (newFiltersForRequest.length === 0) {
        pageRequest.current.filterBy = defaultFilter;
      } else {
        pageRequest.current.filterBy = newFiltersForRequest;
      }
      getDevices();
    }
  }, 1000);

  const onSearchQueryChange = query => {
    pageRequest.current.search = query;
    getDevices();
  };

  const onTextInputChange = e => {
    onSearchQueryChange(e.target.value);
  };

  const onRowSelected = device => {
    const url = getUrlForDevice(device, device.device_type);
    props.onNavigate(url, null, `?lastActivity=${device.timestamp}&kitId=${kitId}`);
  };

  const onRefresh = () => {
    refreshEnable.current = false;
    setTimeout(() => {
      refreshEnable.current = true;
    }, 10000);
    getDevices();
    getKitDetails(kitId);
    props.refreshParent();
  };

  const getHeaderComponents = () => {
    return (
      <React.Fragment>
        <TextInput class="search" placeholder={Strings.search} onChange={onTextInputChange} />
        {hasPermission(PERMISSIONS.DEVICES_SHOW_DELETED) && (
          <AdvancedFilters>
            <Filter definition={getFiltersDefinition().DeviceStatus} />
          </AdvancedFilters>
        )}
        <Button onClick={onKitEdit} title={Strings.edit} />
        <Button class={HEADER_BUTTON_DARK_BLUE} onClick={onKitDelete} title={Strings.delete} />
        <Button class="refresh" disabled={!refreshEnable.current} onClick={onRefresh} />
      </React.Fragment>
    );
  };

  const onKitEdit = () => {
    const data = {
      kitId,
      kitName,
      orgId: org,
      onRefresh,
      action: actions.updateKit,
      actionType: UPDATE_KIT,
    };

    props.openEditModal(data);
  };

  const getKitDetails = id => {
    props.getKitDetails(id).then(resp => {
      if (resp.type === GET_KIT_DETAILS_RESULT) {
        setKitName(resp.response?.name);
        setOrg(resp.response?.org_id);
      }
    });
  };

  const onKitDelete = event => {
    event.preventDefault();
    event.stopPropagation();

    const data = {
      title: (
        <span>
          {Strings.deleteKitWarning} <b>{kitId}</b>?
        </span>
      ),

      onConfirmAction: actions.deleteKit(kitId),
      confirmPostAction: () => props.onNavigate('/devices/Kit'),
      onCancelAction: null,
    };
    props.openConfirmModal(data);
  };

  const onCapsDelete = id => {
    const cap = props.kitDevices[id];
    const deviceId = cap.device_id;
    const data = {
      title: (
        <span>
          {Strings.deleteHubDeviceWarning}{' '}
          <b>{cap.device_id.split('.').length >= 2 ? cap.device_id.split('.')[1] : cap.device_id}</b>?
        </span>
      ),

      onConfirmAction: actions.unassignDeviceFromKit(kitId, deviceId, [
        actions.getKitDevices(kitId, pageRequest.current),
      ]),
      onCancelAction: null,
    };
    props.openConfirmModal(data);
  };

  const renderDeviceId = device => {
    let id = device.device_id;
    if (typeof device.manufacturer !== 'undefined') {
      const prefix = device.manufacturer.concat('.', device.model);
      id = device.device_id.replace(prefix, '');
      if (id.startsWith('.')) id = id.slice(1);
    }
    return id;
  };

  const showingDeleted = pageRequest.current.filterBy.includes('status:deleted');

  const columns = React.useMemo(() => {
    const cols = [
      columnHelper.accessor('device_id', {
        header: Strings.deviceId,
        cell: info => renderDeviceId(info.row.original),
        size: 150,
      }),
      columnHelper.accessor('device_name', {
        header: Strings.deviceName,
        cell: info => info.getValue() || '-',
        size: 150,
      }),
      columnHelper.accessor('deviceModel', {
        header: Strings.deviceModel,
        cell: info => {
          const e = info.row.original;
          return e.manufacturer ? e.manufacturer.concat(' ', e.model) : '-';
        },
        size: 150,
      }),
    ];

    if (!showingDeleted) {
      cols.push(
        columnHelper.accessor('timestamp', {
          header: Strings.lastActivity,
          cell: info => (info.getValue() ? convertUnixEpochToHumanReadable(info.getValue()) : '-'),
          size: 180,
        }),
      );
    }

    cols.push(
      columnHelper.accessor('device_type', {
        header: Strings.deviceType,
        cell: info => info.getValue() || '-',
        size: 120,
      }),
    );

    if (!showingDeleted) {
      cols.push(
        columnHelper.accessor('last_reading', {
          header: Strings.lastReading,
          cell: info => (!_.isEmpty(info.getValue()) ? getVitalsData(info.getValue()) : '-'),
          size: 150,
        }),
      );
    }

    if (showingDeleted) {
      cols.push(
        columnHelper.accessor('assigned_at', {
          header: Strings.assignedDate,
          cell: info => moment(info.getValue()).format(DATE_MONTH_DAY_YEAR_WITH_DASH),
          size: 150,
        }),
        columnHelper.accessor('unassigned_at', {
          header: Strings.unassignedDate,
          cell: info => moment(info.getValue()).format(DATE_MONTH_DAY_YEAR_WITH_DASH),
          size: 150,
        }),
      );
    }

    return cols;
  }, [showingDeleted]);

  const buttons = showingDeleted ? [] : [
    {
      icon: 'delete',
      onClick: onCapsDelete,
      disabled: () => {
        return false;
      },
      text: Strings.delete,
    },
  ];

  return (
    <div className="details-container">
      <PageHeader
        right={() => getHeaderComponents()}
        left={Strings.formatString(
          Strings.showingX,
          props.kitDevices?.length || '-',
          showingDeleted ? Strings.deletedDevices : Strings.devices,
        )}
      />
      <AdvancedFiltersBar />
      {props.kitDevices && (
        <Table3
          isLoading={props.isLoading}
          key={`kit-devices-${showingDeleted ? 'deleted' : 'active'}`}
          name={`kit-devices-${showingDeleted ? 'deleted' : 'active'}`}
          data={props.kitDevices}
          columns={columns}
          onRowClick={onRowSelected}
          buttons={buttons}
          enableColumnFiltering
        />
      )}
    </div>
  );
}

KitDevices.propTypes = {
  clearData: PropTypes.func,
  kitDevices: PropTypes.array,
  history: PropTypes.shape({
    location: PropTypes.shape({ state: PropTypes.shape({ params: PropTypes.shape({ offset: PropTypes.any }) }) }),
  }),
  isLoading: PropTypes.any,
  getKitDevices: PropTypes.func,
  getKitDetails: PropTypes.func,
  match: PropTypes.shape({
    params: PropTypes.shape({ id: PropTypes.any }),
    url: PropTypes.shape({ replace: PropTypes.func }),
  }),
  onNavigate: PropTypes.func,
  openConfirmModal: PropTypes.func,
  openEditModal: PropTypes.func,
  filters: PropTypes.array,
};

const mapStateToProps = state => {
  return {
    role: state.auth.role,
    kitDevices: state.entities.kits?.kitDevices?.devices,
    isLoading: state.entities.kits?.isLoading,
    filters: state.entities.advancedFilters.filters.items,
  };
};

const mapDispatchToProps = dispatch => ({
  onNavigate: (path, params, search) =>
    dispatch(
      push({
        pathname: path,
        state: { params },
        search,
      }),
    ),
  getKitDevices: (kitId, pageRequest) => dispatch(actions.getKitDevices(kitId, pageRequest)),
  clearData: () => dispatch(actions.clearData()),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  openEditModal: data => dispatch(openModalAction('create-kit-and-add-devices', data)),
  getKitDetails: kitId => dispatch(actions.getKitDetails(kitId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(KitDevices);
